/**
 * Upload screen: pick a transport document, push its containers to UTURN.
 *
 * `POST /documents/submit` creates the shipments and, when the server stores extractions,
 * returns a `submission_id`. On success this screen navigates to
 * `/document-reader/{submission_id}`, which reads the persisted submission back and shows
 * the per-order result. When the server does not store extractions there is no id to link
 * to, so a notice is shown here instead.
 */

import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	ACCEPTED_EXTENSIONS,
	fetchHealthConfig,
	type HealthConfig,
	MAX_UPLOAD_BYTES,
	submitDocument,
} from './api';
import { messageOf } from './shared';
import { TokenBridge } from './token-bridge';
import './styles.css';

type Phase = 'idle' | 'submitting' | 'done' | 'error';

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentReader() {
	const navigate = useNavigate();
	const [file, setFile] = useState<File | null>(null);
	const [phase, setPhase] = useState<Phase>('idle');
	const [error, setError] = useState<string | null>(null);
	const [storeOffNotice, setStoreOffNotice] = useState<string | null>(null);
	const [elapsed, setElapsed] = useState(0);
	const [config, setConfig] = useState<HealthConfig | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const busy = phase === 'submitting';

	// Advisory only: the mode chip is nice to have, and a failure here must not block use.
	useEffect(() => {
		let cancelled = false;
		fetchHealthConfig()
			.then((value) => {
				if (!cancelled) setConfig(value);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, []);

	// A submit takes 15-90s, so the elapsed counter is the difference between "working"
	// and "frozen" for whoever is watching.
	useEffect(() => {
		if (!busy) return;
		const started = Date.now();
		setElapsed(0);
		const timer = window.setInterval(() => {
			setElapsed(Math.floor((Date.now() - started) / 1000));
		}, 1000);
		return () => window.clearInterval(timer);
	}, [busy]);

	const onPick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const picked = event.target.files?.[0] ?? null;
		setError(null);
		setStoreOffNotice(null);
		if (picked !== null && picked.size > MAX_UPLOAD_BYTES) {
			// Mirror the backend's own cap rather than firing a request that is certain to 413.
			setFile(null);
			setError(
				`This file is ${formatBytes(picked.size)}. The server accepts at most ${formatBytes(MAX_UPLOAD_BYTES)}.`,
			);
			if (inputRef.current !== null) inputRef.current.value = '';
			return;
		}
		setFile(picked);
	}, []);

	const onSubmit = useCallback(async () => {
		if (file === null) return;

		setError(null);
		setStoreOffNotice(null);
		setPhase('submitting');

		try {
			const submit = await submitDocument(file);
			if (submit.submission_id === null) {
				// Store off: the shipments were created, but there is no persisted record
				// to read back, so there is nothing to navigate to.
				setStoreOffNotice(
					'The shipments were created, but this server does not store submissions (STORE_EXTRACTIONS=false), so the result cannot be shown as a page.',
				);
				setPhase('done');
				return;
			}
			await navigate({
				to: '/document-reader/$submissionId',
				params: { submissionId: submit.submission_id },
			});
		} catch (caught) {
			// The service writes its 413/415/422/502 detail messages for humans; show them as-is.
			setError(messageOf(caught));
			setPhase('error');
		}
	}, [file, navigate]);

	const statusText = busy
		? `Reading document, extraction and push to UTURN — this takes 15 to 90 seconds. (${elapsed}s)`
		: '';

	return (
		<div className="document-reader">
			<TokenBridge />
			<header className="topbar">
				<div className="topbar-inner">
					<div className="brand">
						<h1>Document reader</h1>
						<p>Convert a transport document into UTURN shipments</p>
					</div>
					{config !== null && (
						<dl className="chips" aria-label="Server configuration">
							<div className="chip">
								<dt>UTURN mode</dt>
								<dd>{config.uturn_mode}</dd>
							</div>
							<div className="chip">
								<dt>Model</dt>
								<dd>{config.llm_model}</dd>
							</div>
						</dl>
					)}
				</div>
			</header>

			<main className="main">
				<section className="card" aria-labelledby="upload-heading">
					<h2 id="upload-heading">Upload document</h2>
					<p className="lead">
						Choose a transport document ({ACCEPTED_EXTENSIONS.join(', ')}, at most{' '}
						{formatBytes(MAX_UPLOAD_BYTES)}). Each run reads the document, extracts
						the fields and sends one shipment per container to UTURN.
					</p>

					<div className="uploader">
						<label className="file-label" htmlFor="document">
							<span className="file-button">Choose file</span>
							<input
								id="document"
								ref={inputRef}
								type="file"
								accept={ACCEPTED_EXTENSIONS.join(',')}
								onChange={onPick}
								disabled={busy}
							/>
						</label>
						<span className="file-name">
							{file === null
								? 'No file chosen'
								: `${file.name} · ${formatBytes(file.size)}`}
						</span>
						<button
							type="button"
							className="primary"
							onClick={() => void onSubmit()}
							disabled={file === null || busy}
						>
							Process document
						</button>
					</div>

					<div
						className="status"
						role="status"
						aria-live="polite"
						aria-busy={busy}
					>
						{busy && (
							<>
								<div className="progress" />
								<p className="status-text">{statusText}</p>
							</>
						)}
						{error !== null && (
							<p className="banner banner-error">
								<strong>Processing failed.</strong> {error}
							</p>
						)}
						{storeOffNotice !== null && (
							<p className="banner banner-warn">
								<strong>Result cannot be shown.</strong> {storeOffNotice}
							</p>
						)}
					</div>
				</section>
			</main>
		</div>
	);
}
